// src/screens/ResetPassword.tsx
import { useForm, SubmitHandler } from "react-hook-form";
import { supabase } from "../supabaseClient";
import { useState, useEffect } from "react";
import { Lock } from "lucide-react";
import i18n from "../i18n";

type FormInputs = {
  email: string;
};

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const ResetPassword = () => {
  const { t } = useI18nSafe();

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormInputs>();
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setErrorMsg(null);
    const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
      redirectTo: `${window.location.origin}/update-password`,
    });

    if (error) {
      setErrorMsg(error.message);
    } else {
      setSubmitted(true);
    }
  };

  return (
    <section className="flex flex-col items-center p-6 min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2 mb-6">
          <Lock className="w-6 h-6 text-orange-600 dark:text-orange-400" />
          <h1 className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {t("resetpassword:title")}
          </h1>
        </div>

        {submitted ? (
          <div className="rounded-lg border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4 text-sm">
            {t("resetpassword:success")}
          </div>
        ) : (
          <form className="space-y-3" onSubmit={handleSubmit(onSubmit)} noValidate>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("resetpassword:email_label")}
            </label>
            <input
              {...register("email", { required: true })}
              type="email"
              autoComplete="email"
              placeholder={t("resetpassword:email_placeholder")}
              aria-invalid={!!errors.email}
              className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-700
                         bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                         placeholder-gray-400 dark:placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors"
            />
            {errors.email && (
              <p className="text-red-600 dark:text-red-400 text-sm">
                {t("resetpassword:email_required")}
              </p>
            )}
            {errorMsg && (
              <p className="text-red-600 dark:text-red-400 text-sm">{errorMsg}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-70 disabled:cursor-not-allowed
                         text-white font-semibold py-3 px-6 rounded-lg
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors"
            >
              {isSubmitting ? t("resetpassword:sending") : t("resetpassword:send_link")}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};
